import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../utils/LanguageContext';
import { useBotProtection, BotProtectionFields } from '../utils/useBotProtection';
import axios from 'axios';
import { Mail, MapPin, Phone, Loader } from 'lucide-react';
import { StarIcon, EyeIcon, SparklesIcon, TrendingUpIcon, LightningBoltIcon, CurrencyDollarIcon, UserGroupIcon, MailIcon, LocationMarkerIcon, PhoneIcon } from '@heroicons/react/solid';
import {Helmet} from "react-helmet-async";
import getHelmetConfig from "../utils/helmetConfig";

const API_BASE_URL = 'https://texla.uz/api/v1';

// Skeleton Loader
const SkeletonLoader = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
);

const AboutPage = () => {
    const { t, language } = useLanguage();
    const [aboutData, setAboutData] = useState(null);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ name: '', phone: '', message: '' });
    const bot = useBotProtection();
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        fetchAboutData();
    }, [language]);

    useEffect(() => {
        bot.reset();
    }, []);

    const fetchAboutData = async () => {
        setLoading(true);
        try {
            const [aboutRes, clientsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/about/`).catch(() => ({ data: [] })),
                axios.get(`${API_BASE_URL}/clients/`).catch(() => ({ data: [] }))
            ]);

            // About
            if (aboutRes.data && aboutRes.data.length > 0) {
                const about = aboutRes.data[0];
                setAboutData({
                    ...about,
                    text: about[`text_${language}`] || about.text_uz || about.text,
                    title: about[`title_${language}`] || about.title_uz || about.title
                });
            }

            // Clients
            if (clientsRes.data && clientsRes.data.length > 0) {
                setClients(clientsRes.data.map(client => ({
                    id: client.id,
                    name: client.title || 'Client',
                    logo: client.logo || 'https://via.placeholder.com/150x100'
                })));
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.phone || !formData.message) return;
        if (!bot.validate()) return;

        try {
            await axios.post(`${API_BASE_URL}/contacts/`, {
                name: formData.name,
                phone: formData.phone,
                message: formData.message
            });

            setSubmitted(true);
            setTimeout(() => {
                setSubmitted(false);
                setFormData({ name: '', phone: '', message: '' });
            }, 3000);
        } catch (error) {
            alert(t('modal.error'));
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader className="animate-spin text-gray-400" size={48} />
            </div>
        );
    }

    const helmetConfig = getHelmetConfig(language);
    const pageConfig = helmetConfig.about;

    return (
        <div className="bg-white">
            <Helmet>
                <title>{pageConfig.title}</title>
                <meta name="description" content={pageConfig.description} />
                <meta name="keywords" content={pageConfig.keywords} />
            </Helmet>
            {/* Breadcrumb */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
                    <Link to={"/"} className="hover:text-gray-900">{t('breadcrumb.home')}</Link>
                    <span>/</span>
                    <span className="text-gray-900">{t('header.aboutCompany')}</span>
                </div>
            </div>

            {/* About Section */}
            {aboutData && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Image */}
                        <div>
                            {aboutData.company_image ? (
                                <img
                                    src={aboutData.company_image}
                                    alt={aboutData.title}
                                    className="w-full h-96 object-cover rounded-lg shadow-lg"
                                />
                            ) : (
                                <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-6">
                                {aboutData.title || t('header.aboutCompany')}
                            </h1>
                            <div className="prose prose-lg max-w-none text-gray-600 mb-8">
                                <div dangerouslySetInnerHTML={{ __html: aboutData.text }} />
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Mission, Vision, Values Section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                    {t('about.values')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Mission */}
                    <div className="bg-blue-50 p-8 rounded-lg">
                        <StarIcon className="w-12 h-12 text-blue-600 mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                            {t('about.mission')}
                        </h3>
                        <p className="text-gray-600">
                            {t('about.missionDesc')}
                        </p>
                    </div>

                    {/* Vision */}
                    <div className="bg-green-50 p-8 rounded-lg">
                        <EyeIcon className="w-12 h-12 text-green-600 mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                            {t('about.vision')}
                        </h3>
                        <p className="text-gray-600">
                            {t('about.visionDesc')}
                        </p>
                    </div>

                    {/* Values */}
                    <div className="bg-purple-50 p-8 rounded-lg">
                        <SparklesIcon className="w-12 h-12 text-purple-600 mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                            {t('about.valuesTitle')}
                        </h3>
                        <p className="text-gray-600">
                            {t('about.valuesDesc')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                    {t('about.whyChooseUs')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white border border-gray-200 p-6 rounded-lg text-center hover:shadow-lg transition">
                        <TrendingUpIcon className="w-14 h-14 text-yellow-500 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {t('about.certification')}
                        </h3>
                        <p className="text-gray-600 text-sm">
                            {t('about.certificationDesc')}
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 p-6 rounded-lg text-center hover:shadow-lg transition">
                        <LightningBoltIcon className="w-14 h-14 text-blue-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {t('about.fastDelivery')}
                        </h3>
                        <p className="text-gray-600 text-sm">
                            {t('about.fastDeliveryDesc')}
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 p-6 rounded-lg text-center hover:shadow-lg transition">
                        <CurrencyDollarIcon className="w-14 h-14 text-green-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {t('about.competitivePrice')}
                        </h3>
                        <p className="text-gray-600 text-sm">
                            {t('about.competitivePriceDesc')}
                        </p>
                    </div>

                    <div className="bg-white border border-gray-200 p-6 rounded-lg text-center hover:shadow-lg transition">
                        <UserGroupIcon className="w-14 h-14 text-red-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {t('about.continuousSupport')}
                        </h3>
                        <p className="text-gray-600 text-sm">
                            {t('about.continuousSupportDesc')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Statistics Section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                    {t('about.statistics')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-center justify-items-center">
                    {/* 15+ Years */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-lg w-full max-w-xs text-center">
                        <div className="text-6xl font-bold text-orange-600 mb-2">15+</div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {t('about.yearsExperience')}
                        </h3>
                    </div>

                    {/* 1000+ Clients */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-lg w-full max-w-xs text-center">
                        <div className="text-6xl font-bold text-green-600 mb-2">100+</div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {t('about.satisfiedClients')}
                        </h3>
                    </div>

                    {/* 5000+ Products */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-lg w-full max-w-xs text-center">
                        <div className="text-6xl font-bold text-purple-600 mb-2">1000+</div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {t('about.productsDelivered')}
                        </h3>
                    </div>

                    {/* 20+ Countries */}
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-lg w-full max-w-xs text-center">
                        <div className="text-6xl font-bold text-orange-600 mb-2">20+</div>
                        <h3 className="text-lg font-bold text-gray-900">
                            {t('about.partnersCountries')}
                        </h3>
                    </div>
                </div>
            </section>

            {/* Clients Section */}
            {clients.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                        {t('home.clients')}
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                        {clients.map((client) => (
                            <div
                                key={client.id}
                                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-center hover:shadow-lg transition h-24"
                            >
                                <img
                                    src={client.logo}
                                    alt={client.name}
                                    className="max-h-16 max-w-full object-contain"
                                />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Contact Section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                    {t('about.contactUs')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Address */}
                    <div className="bg-blue-50 p-8 rounded-lg text-center">
                        <MapPin size={40} className="text-orange-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {t('about.address')}
                        </h3>
                        <p className="text-gray-600">
                            {aboutData ? (language === 'uz' ? aboutData.address_uz : aboutData.address_ru) : (language === 'uz' ? 'Tashkent, O\'zbekiston' : 'Ташкент, Узбекистан')}
                        </p>
                    </div>

                    {/* Phone */}
                    <div className="bg-green-50 p-8 rounded-lg text-center">
                        <Phone size={40} className="text-green-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {t('about.phone')}
                        </h3>
                        <p className="text-gray-600">
                            {aboutData ? (
                                <a href={`tel:${aboutData.phone}`} className="hover:text-green-600">
                                    {aboutData.phone}
                                </a>
                            ) : (
                                <a href="tel:+998901234567" className="hover:text-green-600">
                                    +998 90 123 45 67
                                </a>
                            )}
                        </p>
                    </div>

                    {/* Email */}
                    <div className="bg-purple-50 p-8 rounded-lg text-center">
                        <Mail size={40} className="text-purple-600 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {t('about.email')}
                        </h3>
                        <p className="text-gray-600">
                            {aboutData ? (
                                <a href={`mailto:${aboutData.email}`} className="hover:text-purple-600">
                                    {aboutData.email}
                                </a>
                            ) : (
                                <a href="mailto:info@texla.uz" className="hover:text-purple-600">
                                    info@texla.uz
                                </a>
                            )}
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;